import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { ArrowUpIcon } from '@heroicons/react/24/outline';

export default function PrivacyPolicyPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const textColor = theme === 'dark' ? 'text-gray-300' : 'text-gray-700';
  const headingColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const bgColor = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const [showScrollButton, setShowScrollButton] = useState(false);

  const handleGoBack = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(-1);
  };

  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show/hide scroll button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`${bgColor} min-h-screen overflow-y-auto`}>
      <header className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-white border-b border-gray-200'} py-4 sticky top-0 z-10`}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-bold text-blue-600">{t('common.appName')}</Link>
          <a href="#" onClick={handleGoBack} className="text-sm font-medium text-blue-600 hover:underline">{t('navigation.goBack')}</a>
        </div>
      </header>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {showScrollButton && (
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className={`fixed bottom-8 right-8 p-3 rounded-full shadow-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors ${theme === 'dark' ? 'bg-blue-700 hover:bg-blue-800' : ''}`}
              aria-label="Scroll to top"
            >
              <ArrowUpIcon className="w-5 h-5" />
            </button>
          )}
            <h1 className={`text-3xl font-bold mb-6 ${headingColor}`}>{t('pages.privacyPolicy.title')}</h1>
            <p className={`mb-4 ${textColor}`}>{t('pages.privacyPolicy.effectiveDate')}</p>
          
          <p className={`mb-6 ${textColor}`}>
            {t('pages.privacyPolicy.introduction', { appName: t('common.appName') })}
          </p>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.scope.title')}</h2>
            <p className={`${textColor}`}>
              Applies to users of the FlowCXP website, platform, and services ("Services"). 
              Personal data handled as a Controller or Processor depending on context.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.dataCollection.title')}</h2>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>Account Data: Name, email, password, organization, billing.</li>
              <li>Customer Content: All data you upload or create within FlowCXP.</li>
              <li>Usage Data: IP addresses, device/browser details, access times.</li>
              <li>Cookies & Tracking: For analytics, functionality, and personalization.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.dataUse.title')}</h2>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>Deliver and maintain Services, authenticate users</li>
              <li>Process billing and subscriptions</li>
              <li>Improve and analyze usage trends</li>
              <li>Provide customer support and updates</li>
              <li>Compliance and fraud prevention</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.dataSharing.title')}</h2>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>Service Providers: Hosting, payment, analytics</li>
              <li>Legal Requirements: To comply with law or prevent harm</li>
              <li>Affiliates: Within FlowCXP group entities, if any</li>
            </ul>
            <p className={`mt-3 font-medium ${textColor}`}>We never sell personal data.</p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.dataRetention.title')}</h2>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>Retained while account is active</li>
              <li>Archived/deleted after termination, per legal/business needs</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.security.title')}</h2>
            <p className={`${textColor}`}>
              We use industry-standard measures (encryption, access controls). Absolute security is not guaranteed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.userRights.title')}</h2>
            <p className={`mb-3 ${textColor}`}>Depending on your jurisdiction, you may:</p>
            <ul className={`list-disc pl-6 space-y-2 ${textColor}`}>
              <li>Access, correct, delete your data</li>
              <li>Object to processing, request portability</li>
              <li>Opt-out of non-essential tracking</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.internationalTransfers.title')}</h2>
            <p className={`${textColor}`}>
              If applicable, international data transfers (e.g. to Singapore, EEA) are protected by relevant 
              safeguards like Standard Contractual Clauses.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.childrenPrivacy.title')}</h2>
            <p className={`${textColor}`}>
              Not intended for users under 16. We do not knowingly collect data from minors.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.updates.title')}</h2>
            <p className={`${textColor}`}>
              Changes will be communicated via email or platform notice with updated effective date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className={`text-2xl font-semibold mb-3 ${headingColor}`}>{t('pages.privacyPolicy.contact.title')}</h2>
            <p className={`${textColor}`}>
              Questions or data requests? Email <a href="mailto:admin@flowcxp.com" className="text-blue-500 hover:underline">admin@flowcxp.com</a>.
            </p>
          </section>
        </div>
      </div>
      <footer className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-t py-4`}>
        <div className="container mx-auto px-4">
          <div className={`flex items-center justify-between text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            <div>&copy; {new Date().getFullYear()} {t('common.appName')}. {t('common.allRightsReserved')}</div>
            <div className="flex space-x-4">
              <Link to="/privacy-policy" className={`${theme === 'dark' ? 'hover:text-gray-200' : 'hover:text-gray-900'}`}>{t('navigation.privacyPolicy')}</Link>
              <Link to="/terms-of-service" className={`${theme === 'dark' ? 'hover:text-gray-200' : 'hover:text-gray-900'}`}>{t('navigation.termsOfService')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
